import type { SceneAmbienceInput } from "./scene-plan.js";

export type SensorStatus = "off" | "requesting" | "enabled" | "denied" | "unavailable" | "revoked";
export type MotionPermissionName = "accelerometer" | "gyroscope";

export interface SensorController {
  enableFromGesture(): Promise<SensorStatus>;
  status(): SensorStatus;
  snapshot(): SceneAmbienceInput;
  disable(): void;
}

type MotionPermissionDecision = "granted" | "denied";

export interface MotionPermissionApi {
  requestPermission?: () => Promise<MotionPermissionDecision>;
}

export interface MotionPermissionStatus {
  readonly state: PermissionState;
  addEventListener(type: "change", listener: EventListener): void;
  removeEventListener(type: "change", listener: EventListener): void;
}

export interface SensorEnvironment {
  readonly motionTarget: Pick<EventTarget, "addEventListener" | "removeEventListener">;
  readonly motionApi: MotionPermissionApi | undefined;
  readonly queryPermission: (name: MotionPermissionName) => Promise<MotionPermissionStatus | null>;
}

export interface SensorControllerOptions {
  readonly environment?: SensorEnvironment;
  readonly onStatusChange?: (status: SensorStatus) => void;
}

const LOW_PASS_COEFFICIENT = 0.08;
const RAW_ACCELERATION_BOUND = 100;
const PERMISSION_NAMES = Object.freeze(["accelerometer", "gyroscope"] as const);

interface PendingEnable {
  readonly generation: number;
  readonly promise: Promise<SensorStatus>;
  readonly resolve: (status: SensorStatus) => void;
  settled: boolean;
}

interface MotionListenerOwnership {
  readonly generation: number;
  readonly listener: EventListener;
}

interface PermissionListenerOwnership {
  readonly generation: number;
  readonly listener: EventListener;
  readonly permission: MotionPermissionStatus;
}

type PermissionOwnership = Record<MotionPermissionName, PermissionListenerOwnership | null>;
type ObservedPermissions = Record<MotionPermissionName, MotionPermissionStatus | null>;

function defaultEnvironment(): SensorEnvironment {
  const motionApi = typeof DeviceMotionEvent === "undefined"
    ? undefined
    : DeviceMotionEvent as unknown as MotionPermissionApi;
  return {
    motionTarget: typeof window === "undefined" ? new EventTarget() : window,
    motionApi,
    queryPermission: async (name): Promise<MotionPermissionStatus | null> => {
      if (typeof navigator === "undefined" || navigator.permissions === undefined) return null;
      try {
        return await navigator.permissions.query({ name: name as PermissionName });
      } catch {
        return null;
      }
    },
  };
}

function boundedAcceleration(value: number | null | undefined): number {
  if (typeof value !== "number" || !Number.isFinite(value)) return 0;
  return Math.min(RAW_ACCELERATION_BOUND, Math.max(-RAW_ACCELERATION_BOUND, value));
}

function lowPass(current: number, target: number): number {
  const next = current * (1 - LOW_PASS_COEFFICIENT) + target * LOW_PASS_COEFFICIENT;
  return Number.isFinite(next) ? next : current;
}

function createPendingEnable(generation: number): PendingEnable {
  let resolvePromise: (status: SensorStatus) => void = () => undefined;
  const promise = new Promise<SensorStatus>((resolve) => {
    resolvePromise = resolve;
  });
  return { generation, promise, resolve: resolvePromise, settled: false };
}

export function createSensorController(options: SensorControllerOptions = {}): SensorController {
  const environment = options.environment ?? defaultEnvironment();
  let currentStatus: SensorStatus = "off";
  let breeze = 0;
  let rainBias = 0;
  let gravityEstimateX = 0;
  let gravityEstimateY = 0;
  let gravityCalibrated = false;
  let generation = 0;
  let pendingEnable: PendingEnable | null = null;
  let motionOwnership: MotionListenerOwnership | null = null;
  const permissionOwnership: PermissionOwnership = {
    accelerometer: null,
    gyroscope: null,
  };
  const notificationQueue: SensorStatus[] = [];
  let notifying = false;

  const publishStatus = (nextStatus: SensorStatus): void => {
    if (currentStatus === nextStatus) return;
    currentStatus = nextStatus;
    notificationQueue.push(nextStatus);
    if (notifying) return;

    notifying = true;
    try {
      while (notificationQueue.length > 0) {
        const notification = notificationQueue.shift();
        if (notification === undefined) continue;
        try {
          options.onStatusChange?.(notification);
        } catch {
          // Observers cannot own or interrupt sensor lifecycle authority.
        }
      }
    } finally {
      notifying = false;
    }
  };

  const resetAmbience = (): void => {
    breeze = 0;
    rainBias = 0;
    gravityEstimateX = 0;
    gravityEstimateY = 0;
    gravityCalibrated = false;
  };

  const settlePending = (owner: PendingEnable, result: SensorStatus): void => {
    if (owner.settled) return;
    owner.settled = true;
    if (pendingEnable === owner) pendingEnable = null;
    owner.resolve(result);
  };

  const ownsGeneration = (owner: PendingEnable): boolean =>
    !owner.settled && pendingEnable === owner && generation === owner.generation;

  const ownsRequest = (owner: PendingEnable): boolean =>
    ownsGeneration(owner) && currentStatus === "requesting";

  const detachMotion = (): unknown[] => {
    const ownership = motionOwnership;
    if (ownership === null) return [];
    motionOwnership = null;
    try {
      environment.motionTarget.removeEventListener("devicemotion", ownership.listener);
      return [];
    } catch (cause) {
      if (motionOwnership === null) motionOwnership = ownership;
      return [cause];
    }
  };

  const detachPermission = (name: MotionPermissionName): unknown[] => {
    const ownership = permissionOwnership[name];
    if (ownership === null) return [];
    permissionOwnership[name] = null;
    try {
      ownership.permission.removeEventListener("change", ownership.listener);
      return [];
    } catch (cause) {
      if (permissionOwnership[name] === null) permissionOwnership[name] = ownership;
      return [cause];
    }
  };

  const cleanupPermissions = (): unknown[] => [
    ...detachPermission("accelerometer"),
    ...detachPermission("gyroscope"),
  ];

  const cleanupListeners = (): unknown[] => [
    ...detachMotion(),
    ...cleanupPermissions(),
  ];

  const readPermissionState = (permission: MotionPermissionStatus): PermissionState | null => {
    try {
      const state = permission.state;
      return state === "granted" || state === "denied" || state === "prompt" ? state : null;
    } catch {
      return null;
    }
  };

  const observedPermissionsGranted = (observed: ObservedPermissions): boolean =>
    PERMISSION_NAMES.every((name) => {
      const permission = observed[name];
      return permission === null || readPermissionState(permission) === "granted";
    });

  const revokeGeneration = (ownedGeneration: number): void => {
    if (generation !== ownedGeneration
        || (currentStatus !== "requesting" && currentStatus !== "enabled")) return;
    const cancelled = pendingEnable;
    generation += 1;
    resetAmbience();
    publishStatus("revoked");
    if (cancelled !== null && cancelled.generation === ownedGeneration) {
      settlePending(cancelled, "revoked");
    }
    cleanupListeners();
  };

  const terminateRequest = (owner: PendingEnable, terminal: SensorStatus): SensorStatus => {
    if (!ownsGeneration(owner)) return currentStatus;
    generation += 1;
    resetAmbience();
    publishStatus(terminal);
    settlePending(owner, terminal);
    cleanupListeners();
    return terminal;
  };

  const createMotionListener = (ownedGeneration: number): EventListener => (event): void => {
    if (generation !== ownedGeneration || currentStatus !== "enabled") return;
    let motionX: number;
    let motionY: number;
    let nextGravityX = gravityEstimateX;
    let nextGravityY = gravityEstimateY;
    let nextGravityCalibrated = gravityCalibrated;
    try {
      const motion = event as DeviceMotionEvent;
      const linear = motion.acceleration;
      const linearX = linear?.x;
      const linearVertical = linear?.y;
      if ((typeof linearX === "number" && Number.isFinite(linearX))
          || (typeof linearVertical === "number" && Number.isFinite(linearVertical))) {
        motionX = boundedAcceleration(linearX);
        motionY = boundedAcceleration(linearVertical);
      } else {
        const combined = motion.accelerationIncludingGravity;
        const combinedX = combined?.x;
        const combinedY = combined?.y;
        if (!((typeof combinedX === "number" && Number.isFinite(combinedX))
            || (typeof combinedY === "number" && Number.isFinite(combinedY)))) return;
        const rawX = boundedAcceleration(combinedX);
        const rawY = boundedAcceleration(combinedY);
        if (!gravityCalibrated) {
          nextGravityX = rawX;
          nextGravityY = rawY;
          nextGravityCalibrated = true;
          motionX = 0;
          motionY = 0;
        } else {
          nextGravityX = lowPass(gravityEstimateX, rawX);
          nextGravityY = lowPass(gravityEstimateY, rawY);
          motionX = rawX - nextGravityX;
          motionY = rawY - nextGravityY;
        }
      }
    } catch {
      return;
    }

    const nextBreeze = lowPass(breeze, motionX);
    const nextRainBias = lowPass(rainBias, Math.abs(motionY));
    if (![nextBreeze, nextRainBias, nextGravityX, nextGravityY].every(Number.isFinite)) return;
    gravityEstimateX = nextGravityX;
    gravityEstimateY = nextGravityY;
    gravityCalibrated = nextGravityCalibrated;
    breeze = nextBreeze;
    rainBias = nextRainBias;
  };

  const attachPermissionListener = (
    owner: PendingEnable,
    name: MotionPermissionName,
    observed: ObservedPermissions,
  ): boolean => {
    if (!ownsRequest(owner)) return false;
    const permission = observed[name];
    if (permission === null) return true;
    const listener: EventListener = (): void => {
      if (!observedPermissionsGranted(observed)) revokeGeneration(owner.generation);
    };
    const ownership: PermissionListenerOwnership = {
      generation: owner.generation,
      listener,
      permission,
    };
    permissionOwnership[name] = ownership;
    try {
      permission.addEventListener("change", listener);
    } catch {
      terminateRequest(owner, "revoked");
      return false;
    }
    if (!ownsRequest(owner) || permissionOwnership[name] !== ownership) return false;
    if (!observedPermissionsGranted(observed)) {
      terminateRequest(owner, "revoked");
      return false;
    }
    return true;
  };

  const attachMotionListener = (owner: PendingEnable): boolean => {
    if (!ownsRequest(owner)) return false;
    const ownership: MotionListenerOwnership = {
      generation: owner.generation,
      listener: createMotionListener(owner.generation),
    };
    motionOwnership = ownership;
    try {
      environment.motionTarget.addEventListener("devicemotion", ownership.listener);
    } catch {
      terminateRequest(owner, "unavailable");
      return false;
    }
    return ownsRequest(owner) && motionOwnership === ownership;
  };

  const queryPermissions = async (owner: PendingEnable): Promise<ObservedPermissions | null> => {
    const observed: ObservedPermissions = { accelerometer: null, gyroscope: null };
    let failed = false;
    for (const name of PERMISSION_NAMES) {
      if (!ownsRequest(owner)) return null;
      try {
        observed[name] = await environment.queryPermission(name);
      } catch {
        failed = true;
      }
    }
    if (!ownsRequest(owner)) return null;
    if (failed || !observedPermissionsGranted(observed)) {
      terminateRequest(owner, "revoked");
      return null;
    }
    return observed;
  };

  const performEnable = async (owner: PendingEnable): Promise<void> => {
    if (!ownsRequest(owner)) return;
    cleanupListeners();
    if (!ownsRequest(owner)) return;
    if (motionOwnership !== null || PERMISSION_NAMES.some((name) => permissionOwnership[name] !== null)) {
      terminateRequest(owner, "unavailable");
      return;
    }
    if (environment.motionApi === undefined) {
      terminateRequest(owner, "unavailable");
      return;
    }

    let decision: MotionPermissionDecision;
    try {
      const requestPermission = environment.motionApi.requestPermission;
      decision = requestPermission === undefined
        ? "granted"
        : await requestPermission.call(environment.motionApi);
    } catch {
      decision = "denied";
    }
    if (!ownsRequest(owner)) return;
    if (decision !== "granted") {
      terminateRequest(owner, "denied");
      return;
    }

    const observed = await queryPermissions(owner);
    if (observed === null || !ownsRequest(owner)) return;
    for (const name of PERMISSION_NAMES) {
      if (!observedPermissionsGranted(observed)) {
        terminateRequest(owner, "revoked");
        return;
      }
      if (!attachPermissionListener(owner, name, observed)) return;
    }
    if (!ownsRequest(owner) || !observedPermissionsGranted(observed)) {
      if (ownsRequest(owner)) terminateRequest(owner, "revoked");
      return;
    }
    if (!attachMotionListener(owner)) return;
    if (!ownsRequest(owner) || !observedPermissionsGranted(observed)) {
      if (ownsRequest(owner)) terminateRequest(owner, "revoked");
      return;
    }

    publishStatus("enabled");
    if (ownsGeneration(owner) && currentStatus === "enabled") settlePending(owner, "enabled");
  };

  const enableFromGesture = (): Promise<SensorStatus> => {
    if (currentStatus === "enabled") return Promise.resolve(currentStatus);
    if (currentStatus === "requesting" && pendingEnable !== null) return pendingEnable.promise;

    const owner = createPendingEnable(++generation);
    pendingEnable = owner;
    publishStatus("requesting");
    if (ownsRequest(owner)) {
      void performEnable(owner).catch(() => {
        terminateRequest(owner, "denied");
      });
    }
    return owner.promise;
  };

  const disable = (): void => {
    const cancelled = pendingEnable;
    generation += 1;
    resetAmbience();
    publishStatus("off");
    if (cancelled !== null) settlePending(cancelled, "off");
    const failures = cleanupListeners();
    if (failures.length > 0) {
      throw new AggregateError(failures, "Sensor listener cleanup incomplete");
    }
  };

  return Object.freeze({
    enableFromGesture,
    status: (): SensorStatus => currentStatus,
    snapshot: (): SceneAmbienceInput => Object.freeze({
      breeze: Number.isFinite(breeze) ? breeze : 0,
      rainBias: Number.isFinite(rainBias) ? rainBias : 0,
    }),
    disable,
  });
}
